import React from 'react';
import { motion } from 'framer-motion';
import { Target, Shield, Zap, Users, Award, Globe } from 'lucide-react';
import { TechLabel } from '../components/common/TacticalCard';
import { useSiteSettings } from '../contexts/SiteSettingsContext';
import { useLanguage } from '../contexts/LanguageContext';
import { fadeUpVariant, staggerContainer } from '../lib/animations';
import SEO from '../components/common/SEO';

const About = () => {
  const { getSetting } = useSiteSettings();
  const { language, t } = useLanguage();

  // Get CMS values with language support
  const aboutTitle = language === 'en'
    ? getSetting('about_title_en', getSetting('about_title', 'OUR BRAND'))
    : getSetting('about_title', 'MARKAMIZ');
  const aboutSubtitle = language === 'en'
    ? getSetting('about_subtitle_en', getSetting('about_subtitle', 'VAV WEAR Story'))
    : getSetting('about_subtitle', 'VAV WEAR Hikayesi');
  const aboutContent = language === 'en'
    ? getSetting('about_content_en', getSetting('about_content', 'VAV Wear has been a pioneering brand in the tactical and outdoor clothing sector since 2020. Our products, offering maximum performance in harsh conditions, are produced with military-standard tested materials.'))
    : getSetting('about_content', 'VAV Wear, 2020 yılından bu yana taktik ve outdoor giyim sektöründe öncü bir marka olarak faaliyet göstermektedir. Zorlu koşullarda maksimum performans sunan ürünlerimiz, askeri standartlarda test edilmiş malzemelerle üretilmektedir.');
  const aboutImage = getSetting('about_image', 'https://images.unsplash.com/photo-1556906781-9a412961c28c?w=800&q=80');
  const aboutVision = language === 'en'
    ? getSetting('about_vision_en', getSetting('about_vision', 'To be a global brand in tactical clothing and provide reliable products in all conditions.'))
    : getSetting('about_vision', 'Taktik giyim sektöründe global bir marka olmak ve her koşulda güvenilir ürünler sunmak.');
  const aboutValues = language === 'en'
    ? getSetting('about_values_en', getSetting('about_values', 'Quality, Durability, Innovation, Customer Satisfaction'))
    : getSetting('about_values', 'Kalite, Dayanıklılık, Yenilik, Müşteri Memnuniyeti');
  const foundingYear = getSetting('founding_year', '2020');
  const teamSize = getSetting('team_size', '50+');

  const values = aboutValues.split(',').map(v => v.trim());

  return (
    <motion.main 
      className="min-h-screen bg-vav-bg pt-20"
      initial={{ opacity: 0 }}
      animate={{ opacity: 1 }}
      exit={{ opacity: 0 }}
    >
      <SEO 
        title={t('Hakkımızda', 'About Us')}
        description={`VAV Wear - ${aboutSubtitle}. ${aboutContent.substring(0, 150)}...`}
        image={aboutImage}
      />
      
      {/* Hero Section */}
      <section className="relative py-24 bg-vav-bg-paper overflow-hidden">
        <div className="absolute inset-0 bg-grid-tactical bg-grid opacity-5" />
        
        <div className="relative max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            variants={staggerContainer}
            initial="initial"
            animate="enter"
          >
            <motion.div variants={fadeUpVariant}>
              <TechLabel variant="gold" className="mb-4">[{t('HAKKIMIZDA', 'ABOUT US')}]</TechLabel>
            </motion.div>
            <motion.h1 
              className="font-display text-5xl sm:text-6xl lg:text-7xl text-vav-fg mb-6"
              variants={fadeUpVariant}
            >
              {aboutTitle}
            </motion.h1>
            <motion.p 
              className="text-xl text-vav-fg-muted max-w-2xl mx-auto"
              variants={fadeUpVariant}
            >
              {aboutSubtitle}
            </motion.p>
          </motion.div>
        </div>
      </section>

      {/* Story Section */}
      <section className="py-24 bg-vav-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-16 items-center">
            {/* Image */}
            <motion.div
              initial={{ opacity: 0, x: -50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <div className="relative aspect-[4/3] rounded-3xl overflow-hidden">
                <img
                  src={aboutImage}
                  alt="VAV Wear"
                  className="w-full h-full object-cover"
                />
                <div className="absolute inset-0 bg-gradient-to-t from-vav-bg/50 to-transparent" />
                
                {/* Stats Overlay */}
                <div className="absolute bottom-4 left-4 right-4 flex justify-between">
                  <div className="bg-vav-bg/90 backdrop-blur-sm px-4 py-2 rounded-lg border border-vav-border">
                    <span className="block text-2xl font-display text-vav-gold">{foundingYear}</span>
                    <span className="text-xs text-vav-fg-muted">{t('Kuruluş', 'Founded')}</span>
                  </div>
                  <div className="bg-vav-bg/90 backdrop-blur-sm px-4 py-2 rounded-lg border border-vav-border">
                    <span className="block text-2xl font-display text-vav-gold">{teamSize}</span>
                    <span className="text-xs text-vav-fg-muted">{t('Ekip', 'Team')}</span>
                  </div>
                </div>
              </div>
            </motion.div>

            {/* Content */}
            <motion.div
              initial={{ opacity: 0, x: 50 }}
              whileInView={{ opacity: 1, x: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.8 }}
            >
              <TechLabel variant="accent" className="mb-4">[{t('HİKAYEMİZ', 'OUR STORY')}]</TechLabel>
              <h2 className="font-heading text-4xl text-vav-fg mb-6">
                {t('Zorlu Koşullar İçin Tasarlandı', 'Designed for Tough Conditions')}
              </h2>
              <p className="text-vav-fg-muted leading-relaxed mb-6 text-lg">
                {aboutContent}
              </p>
              <p className="text-vav-fg-muted leading-relaxed">
                {t(
                  'Her ürünümüz, saha testlerinden geçirilir ve profesyonellerin geri bildirimleriyle sürekli geliştirilir. VAV Wear, sadece bir giyim markası değil, bir yaşam tarzıdır.',
                  'Every product is field-tested and continuously improved with feedback from professionals. VAV Wear is not just a clothing brand, it\'s a lifestyle.'
                )}
              </p>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Vision & Values */}
      <section className="py-24 bg-vav-bg-paper">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-12">
            {/* Vision */}
            <motion.div
              className="p-8 bg-vav-bg rounded-2xl border border-vav-border"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
            >
              <div className="w-14 h-14 rounded-xl bg-vav-gold/20 flex items-center justify-center mb-6">
                <Target className="w-7 h-7 text-vav-gold" />
              </div>
              <h3 className="font-heading text-2xl text-vav-fg mb-4">{t('VİZYONUMUZ', 'OUR VISION')}</h3>
              <p className="text-vav-fg-muted leading-relaxed">
                {aboutVision}
              </p>
            </motion.div>

            {/* Values */}
            <motion.div
              className="p-8 bg-vav-bg rounded-2xl border border-vav-border"
              initial={{ opacity: 0, y: 30 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: 0.1 }}
            >
              <div className="w-14 h-14 rounded-xl bg-vav-accent-cyan/20 flex items-center justify-center mb-6">
                <Award className="w-7 h-7 text-vav-accent-cyan" />
              </div>
              <h3 className="font-heading text-2xl text-vav-fg mb-4">{t('DEĞERLERİMİZ', 'OUR VALUES')}</h3>
              <div className="flex flex-wrap gap-2">
                {values.map((value, index) => (
                  <span 
                    key={index}
                    className="px-3 py-1.5 bg-vav-bg-subtle rounded-lg text-sm text-vav-fg border border-vav-border"
                  >
                    {value}
                  </span>
                ))}
              </div>
            </motion.div>
          </div>
        </div>
      </section>

      {/* Features */}
      <section className="py-24 bg-vav-bg">
        <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
          <motion.div 
            className="text-center mb-16"
            initial={{ opacity: 0, y: 20 }}
            whileInView={{ opacity: 1, y: 0 }}
            viewport={{ once: true }}
          >
            <TechLabel variant="accent" className="mb-4">[{t('NEDEN VAV WEAR', 'WHY VAV WEAR')}]</TechLabel>
            <h2 className="font-heading text-4xl text-vav-fg">
              {t('BİZİ FARKLI KILAN', 'WHAT MAKES US DIFFERENT')}
            </h2>
          </motion.div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            {[
              { 
                icon: Shield, 
                title: t('Askeri Standartlar', 'Military Standards'), 
                description: t('Tüm ürünlerimiz askeri kalite testlerinden geçer.', 'All our products pass military quality tests.'),
                color: 'vav-gold'
              },
              { 
                icon: Zap, 
                title: t('Yenilikçi Tasarım', 'Innovative Design'), 
                description: t('Sürekli AR-GE ile en son teknolojileri kullanırız.', 'We use the latest technologies with continuous R&D.'),
                color: 'vav-accent-cyan'
              },
              { 
                icon: Globe, 
                title: t('Global Kalite', 'Global Quality'), 
                description: t('Dünya standartlarında üretim ve kalite kontrol.', 'World-class production and quality control.'),
                color: 'vav-accent-green'
              },
            ].map((feature, index) => (
              <motion.div
                key={index}
                className="p-6 bg-vav-bg-paper rounded-2xl border border-vav-border text-center"
                initial={{ opacity: 0, y: 30 }}
                whileInView={{ opacity: 1, y: 0 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.1 }}
              >
                <div className={`w-16 h-16 mx-auto rounded-2xl bg-${feature.color}/20 flex items-center justify-center mb-4`}>
                  <feature.icon className={`w-8 h-8 text-${feature.color}`} />
                </div>
                <h3 className="font-heading text-xl text-vav-fg mb-2">{feature.title}</h3>
                <p className="text-vav-fg-muted text-sm">{feature.description}</p>
              </motion.div>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-16 bg-vav-bg-paper">
        <div className="max-w-4xl mx-auto px-4 text-center">
          <h2 className="font-heading text-3xl text-vav-fg mb-4">
            Koleksiyonumuzu Keşfedin
          </h2>
          <p className="text-vav-fg-muted mb-8">
            Zorlu koşullar için tasarlanmış ürünlerimizi inceleyin.
          </p>
          <a 
            href="/shop" 
            className="tactical-btn inline-flex items-center gap-2"
          >
            MAĞAZAYA GİT
          </a>
        </div>
      </section>
    </motion.main>
  );
};

export default About;
